/**
 * Turns a thrown bulk-action error into the sentence we show the user.
 *
 * Extracted verbatim from the working `catch` in `GuestsDropDown.runBulk` so
 * the sibling surfaces stop being the odd ones out: `EventsDropDown.runBulk`
 * had no `catch` at all (unhandled rejection, dialog open, no text),
 * `useCTA` only wrote to the console, and `PGuestDetails/Actions` left its
 * covering spinner up forever.
 */

import { fieldGateCode } from "@/lib/i18n/translateBackendError";
import { isBackendErrorCode, isServerErr } from "@/lib/api/errors/ErrorHandler";

type Translator = {
  (key: string, values?: Record<string, string | number>): string;
  has: (key: string) => boolean;
};

export type BulkErrorTranslators = {
  /** `useTranslations("errors")` — maps server error CODES to sentences. */
  tErrors: Pick<Translator, "has"> & ((key: string) => string);
  /** `useTranslations("bulkConfirm")` — the generic fallbacks. */
  tBulk: (key: string, values?: Record<string, string | number>) => string;
};

/**
 * A server-action timeout (the platform terminates at 60s) deserves the
 * "refine your filter" hint rather than a generic failure.
 */
function isTimeout(rawMessage: string, status: unknown): boolean {
  return (
    status === 504 ||
    rawMessage === "BULK_TIMEOUT" ||
    rawMessage.includes("504") ||
    rawMessage.toLowerCase().includes("timeout")
  );
}

/**
 * One identifier-like token — a code (`capacityConflict`), a worker key
 * (`worker_rpc_error`), a sentinel (`CAPACITY_EXCEEDED`), a suffixed code
 * (`someCode:detail`) — as opposed to a sentence. A token we have no copy for
 * is shown as the generic line, never as itself; a sentence (something a
 * caller already translated) passes through.
 */
function isToken(rawMessage: string): boolean {
  return /^[A-Za-z][A-Za-z0-9_.:,-]*$/.test(rawMessage);
}

/**
 * One sentence per DISTINCT reason in a partial failure, in the order the
 * server reported them.
 *
 * Without this the dialog could only ever say "N failed", which covers four
 * unrelated situations — no permission, a billing block, someone else got
 * there first, and "it is gone but a follow-up step errored". Two of those the
 * user can act on and two they cannot, and the sentence was identical.
 *
 * `bulkConfirm.reason.*` first, because the generic `errors.*` copy is written
 * for other surfaces — `errors.activeSubscription` is the impersonal "a paid
 * subscription is still running, cancel it under Subscription or write to
 * support" of every reader (PB-CDP), not the reason a single event row of
 * this dialog was refused, which `bulkConfirm.reason.*` names. Anything the
 * server invents later still falls back to `errors.*`, and an entirely unknown
 * code is skipped rather than shown raw.
 */
export function describeBulkFailures(
  failures: ReadonlyArray<{ reason?: string }> | undefined,
  { tErrors, tBulk }: BulkErrorTranslators & { tBulk: Translator },
): string[] {
  const seen = new Set<string>();
  const lines: string[] = [];
  for (const { reason } of failures ?? []) {
    if (!reason || seen.has(reason)) continue;
    seen.add(reason);
    const key = `reason.${reason}`;
    if (tBulk.has(key)) lines.push(tBulk(key));
    else if (tErrors.has(reason)) lines.push(tErrors(reason));
  }
  return lines;
}

/**
 * The one sentence for a refused action on ONE row — the settings dialog,
 * the row menu, a single-item archive. They used to answer every refusal with
 * `bulkConfirm.singleFailure` ("could not be processed, please try again"),
 * which tells the user to retry something that will be refused again. The
 * reason's own sentence when we have one; the generic line only for a code we
 * have no sentence for.
 */
export function describeSingleFailure(
  failures: ReadonlyArray<{ reason?: string }> | undefined,
  translators: BulkErrorTranslators & { tBulk: Translator },
): string {
  return describeBulkFailures(failures, translators)[0] ?? translators.tBulk("singleFailure");
}

/** Codes that say only "something failed" — a 504 status says more. */
const GENERIC_FAILURE_CODES = new Set(["serverError", "unknown"]);

/**
 * axios' own English for a failure without a body ("Request failed with status
 * code 403", "Network Error"). It looks like prose but nobody translated it.
 */
function isAxiosText(rawMessage: string): boolean {
  return /^Request failed with status code \d{3}$/.test(rawMessage) || rawMessage === "Network Error";
}

export function describeBulkError(
  err: unknown,
  { tErrors, tBulk }: BulkErrorTranslators,
): string {
  // Clients THROW an Error; handlers RETURN a ServerError value. Both carry
  // the code on `message` and, since T0, the HTTP status.
  const rawMessage =
    err instanceof Error || isServerErr(err) ? (err.message ?? "") : "";
  const status =
    err && typeof err === "object"
      ? ((err as { status?: unknown }).status ?? (err as { httpStatus?: unknown }).httpStatus)
      : undefined;

  if (isTimeout(rawMessage, undefined)) return tBulk("bulkTimeout");

  // ServerError messages are i18n codes (e.g. "validationError",
  // "advancedConditionsUnsupported") — translate when known. A field-gate
  // refusal in its raw `code:<key>` form reads as its code's sentence, never
  // as the token; any other suffixed code reads as its base code. A specific
  // code beats a 504; a generic one does not. An unknown token and axios'
  // bodiless English are the generic line (both used to be shown raw); a
  // sentence a caller already translated falls through untouched.
  const code = fieldGateCode(rawMessage) ?? rawMessage;
  const known = !!code && tErrors.has(code);
  if (known && !GENERIC_FAILURE_CODES.has(code)) return tErrors(code);
  if (isTimeout("", status)) return tBulk("bulkTimeout");
  if (known) return tErrors(code);
  // `code:detail` — no space after the colon ("message: size must…" is prose)
  // and a real code shape (never a prototype name like `toString`).
  const base = /^([a-z][a-zA-Z0-9]*):\S/.exec(rawMessage)?.[1];
  if (base && isBackendErrorCode(base) && tErrors.has(base)) return tErrors(base);
  if (!rawMessage || isToken(rawMessage) || isAxiosText(rawMessage)) {
    return tBulk("bulkGenericFailure");
  }
  return rawMessage;
}
